import { useState, useMemo, useEffect } from "react";
import { PanelMenuItem } from "../components/PanelMenu";
import useDebounced from "./useDebounced";
import { Input, PropsInput } from "../components";
import { ReactComponent as SearchIcon } from "assets/icons/search.svg";

export interface RouteSearchProps {
    routes: string[];
    menuItem?: PanelMenuItem;
    inputFactory?: (
        searchValue: string,
        changeSearchValue: (newValue: string) => void
    ) => JSX.Element;
    inputProps?: Omit<Partial<PropsInput>, "value" | "onChange">;
    debounceMilliseconds?: number;
}

export const useRouteSearch = ({
    routes,
    menuItem,
    inputFactory,
    inputProps,
    debounceMilliseconds,
}: RouteSearchProps) => {

    const [searchText, setSearchText] = useState(() =>
        routes.reduce((acc, route) => {
            acc[route] = "";
            return acc;
        }, {} as Record<string, string>)
    );

    const currentLink = menuItem?.link;

    useEffect(() => {
        if (!currentLink) return;
        setSearchText((prev) => {
            if (prev[currentLink] === "") return prev;
            return { ...prev, [currentLink]: "" };
        });
    }, [currentLink]);

    const currentItemSearch = useMemo(
        () => (currentLink ? searchText[currentLink] : ""),
        [currentLink, searchText]
    );

    const debouncedSearch = useDebounced(
        currentItemSearch,
        debounceMilliseconds ?? 500
    );

    const searchElement = useMemo(() => {
        if (!currentLink || !routes.length || !routes.includes(currentLink))
            return null;

        const searchValue = searchText[currentLink];
        const changeSearchValue = (newValue: string) =>
            setSearchText((previous) => ({
                ...previous,
                [currentLink]: newValue,
            }));

        return inputFactory ? (
            inputFactory(searchValue, changeSearchValue)
        ) : (
            <Input
                key={currentLink} 
                id="search"
                name="search"
                errorMessage=""
                type="text"
                isValid
                size="medium"
                rightIcon={<SearchIcon />}
                {...inputProps}
                value={searchValue}
                onChange={(e) => changeSearchValue(e.target.value)}
            />
        );
    }, [inputFactory, inputProps, currentLink, routes, searchText]);

    useEffect(() => {
        const iframe = document.getElementsByTagName("iframe")[0];
        if (iframe && iframe.src && iframe.src !== "about:blank") {
            iframe.contentWindow?.postMessage(
                { type: "search", data: { search: debouncedSearch } },
                iframe.src
            );
        }
    }, [debouncedSearch]);

    return { debouncedSearch, searchElement };
};


